import React from 'react';
import { X, Clock } from 'lucide-react';
import { SlotCard } from './SlotCard';
import { BookingDate, Slot } from '../../types';

interface SlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: BookingDate | null;
  slots: Slot[];
  loadingSlots: boolean;
  onSlotSelect: (slotConsultantMappingId: string) => void;
}

export const SlotModal: React.FC<SlotModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  slots,
  loadingSlots,
  onSlotSelect,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Choose a time</h3>
            <p className="text-sm text-gray-600">
              {selectedDate 
                ? `Available slots for ${selectedDate.date ? new Date(selectedDate.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}`
                : 'Select a date to view available time slots'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingSlots ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="rounded-lg border-2 p-4 bg-gray-50">
                    <div className="flex justify-between mb-3">
                      <div className="h-5 bg-gray-100 rounded w-16"></div>
                      <div className="h-5 bg-gray-100 rounded w-12"></div>
                    </div>
                    <div className="h-4 bg-gray-100 rounded mb-3 w-24"></div>
                    <div className="h-10 bg-gray-100 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : slots.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">No available slots for this date</p>
            </div>
          ) : (
            <div className="space-y-4">
              {slots.map((slot) => (
                <SlotCard
                  key={slot._id}
                  slot={slot}
                  onSlotSelect={onSlotSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
